/**
 * useSectors -- Sector Analysis store (Zustand).
 */
import { create } from 'zustand'
import {
  getSectorPerformance,
  getSectorRotationData,
  getSectorTopStocks,
  getSectorCorrelation,
} from '@/lib/api'
import type {
  SectorPerformanceResponse,
  SectorRotationResponse,
  SectorTopStocksResponse,
  SectorCorrelationResponse,
} from '@/lib/types'

interface SectorState {
  // Performance
  performance: SectorPerformanceResponse | null
  performanceLoading: boolean
  performanceError: string | null
  period: string
  setPeriod: (p: string) => void
  fetchPerformance: () => Promise<void>

  // Rotation
  rotation: SectorRotationResponse | null
  rotationLoading: boolean
  rotationError: string | null
  fetchRotation: () => Promise<void>

  // Top Stocks
  topStocks: SectorTopStocksResponse | null
  topStocksLoading: boolean
  topStocksError: string | null
  selectedSector: string | null
  selectSector: (etf: string) => void
  fetchTopStocks: (etf: string) => Promise<void>

  // Correlation
  correlation: SectorCorrelationResponse | null
  correlationLoading: boolean
  correlationError: string | null
  fetchCorrelation: () => Promise<void>
}

export const useSectors = create<SectorState>()((set, get) => ({
  // Performance
  performance: null,
  performanceLoading: false,
  performanceError: null,
  period: '1m',

  setPeriod: (period) => {
    set({ period })
    get().fetchPerformance()
  },

  fetchPerformance: async () => {
    const { period } = get()
    set({ performanceLoading: true, performanceError: null })
    try {
      const data = await getSectorPerformance(period)
      set({ performance: data, performanceLoading: false })
    } catch (e) {
      set({ performanceError: String(e), performanceLoading: false })
    }
  },

  // Rotation
  rotation: null,
  rotationLoading: false,
  rotationError: null,

  fetchRotation: async () => {
    set({ rotationLoading: true, rotationError: null })
    try {
      const data = await getSectorRotationData()
      set({ rotation: data, rotationLoading: false })
    } catch (e) {
      set({ rotationError: String(e), rotationLoading: false })
    }
  },

  // Top Stocks
  topStocks: null,
  topStocksLoading: false,
  topStocksError: null,
  selectedSector: null,

  selectSector: (etf) => {
    set({ selectedSector: etf })
    get().fetchTopStocks(etf)
  },

  fetchTopStocks: async (etf) => {
    set({ topStocksLoading: true, topStocksError: null })
    try {
      const data = await getSectorTopStocks(etf)
      set({ topStocks: data, topStocksLoading: false })
    } catch (e) {
      set({ topStocksError: String(e), topStocksLoading: false })
    }
  },

  // Correlation
  correlation: null,
  correlationLoading: false,
  correlationError: null,

  fetchCorrelation: async () => {
    set({ correlationLoading: true, correlationError: null })
    try {
      const data = await getSectorCorrelation()
      set({ correlation: data, correlationLoading: false })
    } catch (e) {
      set({ correlationError: String(e), correlationLoading: false })
    }
  },
}))
